import UTIF from 'utif'
import { MAX_SOURCE_PAGES } from './segments'

export const PDF_TYPE = 'application/pdf'

// A decoded page is held as RGBA, so a page past this many pixels (about a 600 DPI letter scan) is refused
// rather than freezing the tab.
const MAX_PAGE_PIXELS = 40_000_000

export const isPdf = (file) => file.type === PDF_TYPE

export const isTiff = (file) => /^image\/tiff?$/.test(file.type) || /\.tiff?$/i.test(file.name)

// Browsers can draw these in an <img> and a canvas; TIFF is the exception outside Safari.
export const isBrowserImage = (file) => file.type.startsWith('image/') && !isTiff(file)

export class SourceError extends Error {}

// A photo or scan as a one-page document. The phone's EXIF rotation is applied, so the page is upright as shown.
export const loadImageSource = async (file) => {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  return {
    kind: 'image',
    pageCount: 1,
    pages: [{ url: URL.createObjectURL(file), width: bitmap.width, height: bitmap.height, bitmap }]
  }
}

const canvasToPng = (canvas) =>
  new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new SourceError('A page of this TIFF could not be read.'))), 'image/png')
  )

// Decodes each TIFF page once into a PNG, which the browser can show and crop like a photo.
export const loadTiffSource = async (file) => {
  const buffer = await file.arrayBuffer()
  const ifds = UTIF.decode(buffer).filter((ifd) => ifd.t256 && ifd.t257)
  if (ifds.length === 0) throw new SourceError('This TIFF has no pages the browser can read.')
  if (ifds.length > MAX_SOURCE_PAGES) {
    throw new SourceError(`This file has ${ifds.length} pages; the maximum is ${MAX_SOURCE_PAGES}.`)
  }

  const pages = []
  for (const ifd of ifds) {
    const width = ifd.t256[0]
    const height = ifd.t257[0]
    if (width * height > MAX_PAGE_PIXELS) {
      throw new SourceError('A page of this TIFF is too large to open here. Scan it at 300 DPI or less.')
    }

    UTIF.decodeImage(buffer, ifd, ifds)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    canvas.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(UTIF.toRGBA8(ifd).buffer), width, height), 0, 0)
    ifd.data = null
    const blob = await canvasToPng(canvas)
    pages.push({ url: URL.createObjectURL(blob), width, height, blob })
  }

  return { kind: 'image', pageCount: pages.length, pages }
}

export const loadSource = (file) => (isTiff(file) ? loadTiffSource(file) : loadImageSource(file))

export const releaseImageSource = (source) =>
  source?.pages.forEach((page) => {
    URL.revokeObjectURL(page.url)
    page.bitmap?.close?.()
  })

export const imagePagePreviewer = (source, maxSize) => async (pageNumber) => {
  const { bitmap, blob, width, height } = source.pages[pageNumber - 1]
  const scale = Math.min(1, maxSize / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)
  canvas.getContext('2d').drawImage(bitmap || (await createImageBitmap(blob)), 0, 0, canvas.width, canvas.height)
  return { canvas, scale }
}

// Images are drawn at their own resolution: one page unit is one pixel.
export const imagePageRenderer = (source) => async (pageNumber) => {
  const { bitmap, blob, width, height } = source.pages[pageNumber - 1]
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d').drawImage(bitmap || (await createImageBitmap(blob)), 0, 0)
  return { canvas, scale: 1 }
}
